/* AdvancesCrudCard.tsx
    Une card affichant la gestion des acomptes
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import { AdvanceData, advancesApi } from 'features/advances';
import { faMoneyBills } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface AdvancesCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// AdvancesCrudCard component
export const AdvancesCrudCard: React.FC<AdvancesCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/Advances',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: AdvanceData) => a.id }]
			: []),
		// Colonne "Référence"
		{
			label: 'Référence',
			field: 'referenceNumber',
			render: (a: AdvanceData) => a.referenceNumber || '',
		},
		// Colonne "Montant"
		{
			label: 'Montant',
			field: 'amount',
			render: (a: AdvanceData) =>
				new Intl.NumberFormat('fr-FR', {
					style: 'currency',
					currency: 'EUR',
				}).format(Number(a.amount ?? 0)),
		},
		//Colonne "Affaire"
		...(extended
			? [
					{
						label: 'Affaire',
						field: 'project.referenceNumber',
						render: (a: AdvanceData) =>
							a.project ? a.project.referenceNumber : '',
						linkUrl: (a: AdvanceData) => `/app/projects/${a.project?.id}/edit`,
					},
				]
			: []),
		// Colonne "Facture client" uniquement si extended
		...(extended
			? [
					{
						label: 'Facture client',
						field: 'customerInvoice.referenceNumber',
						render: (a: AdvanceData) =>
							a.customerInvoice?.referenceNumber || '',
						linkUrl: (a: AdvanceData) =>
							`/app/CustomerInvoices/${a.customerInvoice?.id}/edit`,
					},
				]
			: []),
	];

	// Rendu du composant GenericCrudCard avec les props appropriées
	return (
		<GenericCrudCard<AdvanceData>
			queryKey="advances"
			title={size === 'sm' ? 'Acomptes' : 'Liste des acomptes'}
			fetchData={(token, options) => advancesApi.listPaginated(token, options)}
			deleteItem={advancesApi.delete}
			tableColumns={columns}
			addButtonLabel="Ajouter un acompte"
			getAddPath={() => '/app/advances/new'}
			getEditPath={(id) => `/app/advances/${id}/edit`}
			emptyMessage="Aucun acompte trouvé."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faMoneyBills : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default AdvancesCrudCard;
